import React, { useContext, useEffect, useState } from 'react';
import {
  Badge,
  ContanierSection,
  Divider,
  InputNumberForm,
  InputTextForm,
  Loading,
  Money,
  Subtitle,
  Toast,
} from '../../uiComponents/common';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { formatDateTranslation, getLocale } from '../../helpers/dates';
import { Show, Table, TableCell } from '../../components';
import { useTranslation } from 'react-i18next';
import { usePagination, useUser } from '../../hooks';
import { ItemIcon } from '../../uiComponents/icons';
import { hasAllowedRoles } from '../../helpers/roles';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Plus, Trash } from 'lucide-react';
import { useMutation, useQuery } from 'react-query';
import { WelletContext } from '@/context/wellet/welletContext';
import { getReservationProducts, postAddProduct } from '@/services';
import { defaultQueryOptions } from '@/utils/defaultQueryOptions';
import ModalDeleteProduct from './request/modalDeleteProduct';
import { creditCardLogo } from '@/helpers/cdn';

const ConditionalWrapper = ({ condition, wrapper, children }) =>
  condition ? wrapper(children) : children;

const ReservationProductZone = ({
  reservation,
  showPayment = true,
  showProduct = true,
  canAddProduct = false,
  onAddSuccess = null,
}) => {
  const language = useSelector((state) => state.app.language);
  const { t } = useTranslation('translation', { keyPrefix: 'components.reservationProductZone' });
  const locale = getLocale(language);
  const { roles } = useUser();
  const { currentPage, setTotalAndReset } = usePagination(1, 1);
  const currency = useSelector((state) => state.app.selectedOrganization);
  const welletContext = useContext(WelletContext);
  const methods = useForm();
  const [openDelete, setOpenDelete] = useState(null);
  const reservationId = reservation?.id ? reservation?.id : reservation?.actualReservation?.id;

  const itemsQuery = useQuery({
    queryKey: ['reservation-products', reservationId],
    queryFn: () => getReservationProducts(welletContext, reservationId),
    ...defaultQueryOptions,
  });

  const addProductMutation = useMutation({
    mutationFn: (body) => postAddProduct(welletContext, reservation?.actualReservation?.id, body),
    onSuccess: () => {
      itemsQuery?.refetch();
      onAddSuccess();
      methods.reset();
      methods.reset({ name: '' });
    },
  });

  useEffect(() => {
    if (reservation?.paymentDetail?.length > 0) {
      setTotalAndReset(reservation?.paymentDetail?.length);
    }
  }, []);

  if (!reservation.reservationDetail || reservation.reservationType === 'RESTAURANT') {
    return null;
  }

  const reservationPaymentDetail =
    reservation.paymentDetail !== null ? reservation?.paymentDetail[currentPage - 1] : null;

  function transformOrderItems(orderItems = [], orderDetails = []) {
    if (orderItems === null) return [];
    let transformedItems = [];
    [...orderItems, ...orderDetails].forEach((item) => {
      transformedItems.push(item);

      if (item.extraPaxs > 0) {
        transformedItems.push({
          productId: `${item.productId}-extra`,
          productName: item.productName,
          quantity: item.extraPaxs,
          paxsPerItem: 0,
          extraPaxs: 0,
          amountPerUnit: item.amountPerExtraPax,
          amountPerExtraPax: item.amountPerExtraPax,
          consumptionAmount: 0,
          totalAmountExtraPax: item.totalAmountExtraPax,
          totalAmountPerUnit: item.amountPerExtraPax,
          totalAmount: item.totalAmountExtraPaxs,
          isExtraPax: true,
        });
      }
    });

    return transformedItems;
  }

  const isWelletFinance = hasAllowedRoles(roles, ['wellet_finance']);

  const depositBadge = (type, isWelletFinance) => {
    switch (type) {
      case 'OK':
        return <Badge icon={'check'} type={'green'} text={t('guaranteeDeposit.paid')} />;
      case 'partially_refunded':
        return <Badge type={'red'} text={t('guaranteeDeposit.partiallyRefunded')} />;
      case 'PARTIALLY_REFUNDED':
        return <Badge type={'red'} text={t('guaranteeDeposit.partiallyRefunded')} />;
      case 'REFUNDED':
        return <Badge type={'red'} text={t('guaranteeDeposit.refunded')} />;
      case 'DISPUTED':
        return <Badge type={'red'} text={t('guaranteeDeposit.disputed')} />;
      case 'DISPUTED_NOT_ACCEPTED':
        return <Badge type={'orange'} text={t('guaranteeDeposit.disputedNotAccepted')} />;
      case 'DEADLINE_MISSIED':
        return <Badge type={'orange'} text={t('guaranteeDeposit.disputedMissed')} />;
      case 'DISPUTED_LOST':
        return <Badge type={'red'} text={t('guaranteeDeposit.disputedLost')} />;
      case 'DISPUTED_WINNED':
        return <Badge type={'green'} text={t('guaranteeDeposit.disputedWinned')} />;
      case 'STRIPE_REVIEW':
        if (isWelletFinance) {
          return <Badge type={'violet'} text={t('guaranteeDeposit.disputedStripeReview')} />;
        } else return <Badge type={'orange'} text={t('guaranteeDeposit.disputedNotAccepted')} />;

      default:
        return <Badge text={type} />;
    }
  };

  const totalPayAmount = (paymentDetails) => {
    if (paymentDetails === null) return 0;
    return paymentDetails.reduce((total, item) => {
      return total + (item.amount || 0);
    }, 0);
  };

  const totalRefundedAmount = (paymentDetails) => {
    if (paymentDetails === null) return 0;
    return paymentDetails.reduce((total, item) => {
      return total + (item.refundedAmount || 0);
    }, 0);
  };

  const totalNetAmount = (paymentDetails) => {
    if (paymentDetails === null) return 0;
    return paymentDetails.reduce((total, item) => {
      return total + (item.currentNetAmount || 0);
    }, 0);
  };

  const onSubmit = (data) => {
    let body = {
      amount: data?.price ?? 0,
      quantity: data?.qty,
      name: data?.name,
    };
    return addProductMutation.mutate(body);
  };

  return (
    <div className='container-section'>
      {showProduct ? (
        <div className='mb-3'>
          {itemsQuery?.data?.data?.products?.map((o) => (
            <div className='mt-1' key={o.productId + 'P'}>
              <div className='row'>
                <div style={{ fontSize: '24px' }} className='col mt-1 text-truncate fw-700'>
                  {o.productName} {o.paxsPerItem >= 2 && ` (${o.paxsPerItem} ${t('guests')})`} x{' '}
                  {o.quantity}
                </div>
              </div>
              {o.extraPaxs > 0 ? (
                <div className='row'>
                  <div style={{ fontSize: '24px' }} className='col mt-1 text-truncate fw-700'>
                    {o.productName} - {t('extraGuests')} {o.extraPaxs}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {showPayment ? (
        <>
          <FormProvider {...methods}>
            <ConditionalWrapper
              condition={canAddProduct}
              wrapper={(children) => (
                <form onSubmit={methods.handleSubmit(onSubmit)}>{children}</form>
              )}
            >
              <Subtitle marginBottom='mb-3' text={<div className='mt-3 mb-0'>{t('title')}</div>} />
              <Show>
                <Show.When isTrue={itemsQuery.isLoading}>
                  <div className='py-4'>
                    <Loading global={false} />
                  </div>
                </Show.When>
                <Show.Else>
                  <ContanierSection maxWidth='100%'>
                    <div>
                      <Table
                        hasPagination={false}
                        borderTop={false}
                        showPagination={false}
                        columns={[
                          { title: 'Items' },
                          { title: t('qty.label') },
                          { title: t('price.label') },
                          { title: 'Total' },
                          { title: '' },
                        ]}
                        pagination='normal'
                        borderContainer={true}
                        header='white'
                        showText={false}
                        data={transformOrderItems(
                          itemsQuery?.data?.data?.products ?? [],
                          // reservation?.reservationDetail?.orderItems,
                          // reservation?.orderDetail ?? [],
                        )}
                        paddingTh={'padding-white-header'}
                        renderLastRow={
                          canAddProduct
                            ? () => (
                                <>
                                  <TableCell>
                                    <div className='2xl:tw-w-1/2'>
                                      <InputTextForm
                                        minLength={0}
                                        maxLength={512}
                                        isRequired={true}
                                        label={t('name.label')}
                                        placeholder={t('name.placeholder')}
                                        name={'name'}
                                      />
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {' '}
                                    <InputNumberForm
                                      name={`qty`}
                                      isRequired={true}
                                      label={t('qty.label')}
                                      placeholder={t('qty.placeholder')}
                                      className='hideArrow tw-p-2'
                                      minNumber={1}
                                      onlyMinNumber
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {' '}
                                    <InputNumberForm
                                      name={`price`}
                                      label={t('price.label')}
                                      placeholder={t('price.placeholder')}
                                      isRequired={true}
                                      currencySymbol={
                                        currency?.currency ? `${currency?.currency} $` : '$'
                                      }
                                      className='hideArrow tw-p-2'
                                      acceptNegative={true}
                                    />
                                  </TableCell>
                                  <TableCell className={'text-right'}>
                                    <Button
                                      className='tw-w-full tw-mt-[27px] md:tw-w-auto'
                                      disabled={!addProductMutation.isIdle}
                                    >
                                      <Plus className='tw-h-4 tw-w-4 tw-mr-2' />
                                      {t('add')}
                                    </Button>
                                  </TableCell>
                                </>
                              )
                            : null
                        }
                        renderRow={(item) => (
                          <>
                            <TableCell className={'fw-700'}>
                              <div className='d-flex align-items-center'>
                                <span className='mr-2'>
                                  <ItemIcon />
                                </span>
                                {item.productName} {item.isExtraPax && `- ${t('extraGuests')}`}{' '}
                                {item.paxsPerItem >= 2 && ` (${item.paxsPerItem} ${t('guests')})`}
                                {item.specialType === 'OPEN_BAR_HOURS' &&
                                  `- ${t('hours', { hours: item.hours })}`}
                              </div>
                            </TableCell>
                            <TableCell style={{ width: '150px' }} className={'dark-grey'}>
                              {item.quantity}
                            </TableCell>
                            <TableCell className={'dark-grey'} style={{ width: '200px' }}>
                              <Money
                                currencyClass={'dark-grey'}
                                value={item.totalAmountPerUnit}
                                showCurrencyEnd={false}
                                currencyCode={currency.currency}
                              />
                            </TableCell>
                            <TableCell className={'dark-grey'} style={{ width: '0px' }}>
                              <Money
                                currencyClass={'dark-grey'}
                                value={
                                  item.isExtraPax
                                    ? item.totalAmount
                                    : item.totalAmountWithoutExtraPaxs
                                }
                                showCurrencyEnd={false}
                                currencyCode={currency.currency}
                              />
                            </TableCell>

                            <TableCell className={'text-right'}>
                              {item.productType === 'CUSTOM' && canAddProduct ? (
                                <Button
                                  type='button'
                                  size={'icon'}
                                  variant={'ghost'}
                                  onClick={() => setOpenDelete(item.id)}
                                >
                                  <Trash className='tw-w-4 tw-h-4' />
                                </Button>
                              ) : null}
                            </TableCell>
                          </>
                        )}
                        rowClass='not-hover'
                      />
                      <div className='row mt-3 px-3 dark-grey'>
                        <div
                          style={{ maxWidth: '200px', width: '100%' }}
                          className='col-auto text-left'
                        >
                          {t('credit')}
                        </div>
                        <div className='col  text-left'>
                          (
                          <Money
                            value={
                              itemsQuery?.data?.data?.consumptionAmount
                                ? itemsQuery?.data?.data?.consumptionAmount
                                : 0
                            }
                          />
                          )
                        </div>

                        <div className='col text-right'>{t('subTotal')}</div>
                        <div
                          style={{ maxWidth: '200px', width: '100%' }}
                          className='col-auto  text-right'
                        >
                          <Money
                            value={
                              itemsQuery?.data?.data?.subTotalAmount
                                ? itemsQuery?.data?.data?.subTotalAmount
                                : 0
                            }
                          />
                        </div>
                      </div>
                      <div className='row mt-1 pr-3 dark-grey '>
                        <div className='col  text-right'>{t('tip')}</div>
                        <div
                          style={{ maxWidth: '200px', width: '100%' }}
                          className='col-auto  text-right'
                        >
                          <Money
                            value={
                              itemsQuery?.data?.data?.tipAmount
                                ? itemsQuery?.data?.data?.tipAmount
                                : 0
                            }
                          />
                        </div>
                      </div>
                      <div className='row mt-1 pr-3 dark-grey'>
                        <div className='col text-right'>{t('tax')}</div>
                        <div
                          style={{ maxWidth: '200px', width: '100%' }}
                          className='col-auto  text-right'
                        >
                          <Money
                            value={
                              itemsQuery?.data?.data?.taxes ? itemsQuery?.data?.data?.taxes : 0
                            }
                          />
                        </div>
                      </div>
                      <div className='row mt-1 fw-700 pr-3 fs-16 dark-grey'>
                        <div className='col text-right'>{t('total')}</div>
                        <div
                          style={{ maxWidth: '200px', width: '100%' }}
                          className='col-auto  text-right'
                        >
                          <Money
                            value={
                              itemsQuery?.data?.data?.totalAmount
                                ? itemsQuery?.data?.data?.totalAmount
                                : 0
                            }
                          />
                        </div>
                      </div>

                      <div className='row mt-1 pr-3 dark-grey'>
                        <div className='col text-right'>{t('depositReservation')}</div>
                        <div
                          style={{ maxWidth: '200px', width: '100%' }}
                          className='col-auto  text-right'
                        >
                          <Money
                            value={
                              itemsQuery?.data?.data?.depositAmount
                                ? itemsQuery?.data?.data?.depositAmount
                                : 0
                            }
                          />
                        </div>
                      </div>
                      {reservationPaymentDetail === null && // <- VER ESTA CONDICION
                      reservation.status !== 'REJECTED' &&
                      reservation.status !== 'CANCELLED' &&
                      reservation.status !== 'PENDING' ? (
                        <div className='row mt-1 pr-3 dark-grey'>
                          <div className='col text-right'>{t('guaranteeExpires')}</div>
                          <div
                            style={{ maxWidth: '200px', width: '100%' }}
                            className='col-auto  text-right text-capitalize'
                          >
                            {reservation?.reservationDetail?.orderExpirationDate
                              ? format(
                                  new Date(reservation?.reservationDetail?.orderExpirationDate),
                                  'EEEE dd MMM. HH:mma',
                                  { locale },
                                )
                              : '-'}
                          </div>
                        </div>
                      ) : null}
                    </div>
                    <Subtitle
                      marginBottom='mb-3'
                      text={<div className='mt-3 mb-0'>{t('payments.title')}</div>}
                    />
                    <div className='row no-gutters container-data mb-26'>
                      <div className='col-auto pr-5 mr-5'>
                        <div className='dark-grey container-label'>{t('payments.total')}</div>
                        <div className='d-flex justify-content-start fw-600 fs-16'>
                          <Money
                            currencyClass={'fw-600 fs-16'}
                            showCurrencyEnd={false}
                            value={totalPayAmount(reservation?.paymentDetail)}
                            currencyCode={currency.currency}
                          />
                        </div>
                      </div>
                    </div>
                    <Show.When
                      isTrue={reservation?.paymentDetail?.some(
                        (p) => p.status === 'PARTIALLY_REFUNDED' || p.status === 'REFUNDED',
                      )}
                    >
                      <Subtitle
                        className={'tw-border-b tw-pb-2 tw-mb-2'}
                        text={
                          <div className='tw-font-semibold tw-text-base'>
                            {t('paymentBreakdown.title')}
                          </div>
                        }
                      />
                      <ContanierSection maxWidth='1200px'>
                        <div className='row mb-2'>
                          <div className='col-auto'>{t('paymentBreakdown.paymentAmount')}</div>
                          <div className='col text-right'>
                            <Money
                              currencyCode={reservation.currency}
                              value={totalPayAmount(reservation?.paymentDetail)}
                            />
                          </div>
                        </div>
                        <Divider />
                        <div className='row my-2'>
                          <div className='col-auto'>{t('paymentBreakdown.refundedAmount')}</div>
                          <div className='col text-right'>
                            {totalRefundedAmount(reservation?.paymentDetail) > 0 ? (
                              <>
                                -{' '}
                                <Money
                                  currencyCode={reservation?.currency}
                                  value={totalRefundedAmount(reservation?.paymentDetail)}
                                />
                              </>
                            ) : (
                              '-'
                            )}
                          </div>
                        </div>
                        <Divider />
                        <div className='row mt-2'>
                          <div className='col-auto fw-600'>{t('paymentBreakdown.netAmount')}</div>
                          <div className='col text-right fw-600'>
                            <Money
                              currencyCode={reservation?.currency}
                              value={totalNetAmount(reservation?.paymentDetail)}
                            />
                          </div>
                        </div>
                      </ContanierSection>
                    </Show.When>
                    <div className='mb-2 fw-700'>{t('payments.subtitle')}</div>
                    {reservation?.paymentDetail?.length > 0 ? (
                      <Table
                        hasPagination={false}
                        borderTop={false}
                        showPagination={false}
                        columns={[
                          { title: t('payments.table.date') },
                          { title: t('payments.table.status') },
                          { title: t('payments.table.amount') },
                          { title: '' },
                        ]}
                        pagination='normal'
                        borderContainer={true}
                        header='white'
                        showText={false}
                        data={reservation?.paymentDetail}
                        paddingTh={'padding-white-header'}
                        renderRow={(item) => {
                          const { dateTime } = formatDateTranslation(item.date, language, 'small');
                          return (
                            <>
                              <TableCell className={'dark-grey sentences-capitalize'}>
                                {dateTime}
                              </TableCell>
                              <TableCell>
                                {depositBadge(
                                  item.statusDetail === 'partially_refunded'
                                    ? item.statusDetail
                                    : item.status,
                                  isWelletFinance,
                                )}
                              </TableCell>
                              <TableCell className={'dark-grey'}>
                                <Money
                                  currencyClass={'dark-grey'}
                                  showCurrencyEnd={false}
                                  currencyCode={currency.currency}
                                  value={item.amount}
                                />
                                {item?.refundedAmount && item.refundedAmount !== item.amount ? (
                                  <div className='d-flex'>
                                    <div className='dark-grey fw-600'>
                                      {t('guaranteeDeposit.refundedAmount')} &nbsp;
                                    </div>

                                    {item?.refundedAmount ? (
                                      <Money
                                        value={item.refundedAmount}
                                        currencyCode={currency.currency}
                                        showCurrencyEnd={false}
                                        currencyClass={'fw-600'}
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </div>
                                ) : null}
                              </TableCell>
                              <TableCell className={'dark-grey'}>
                                {item.brand ? (
                                  <>
                                    <span
                                      className='mr-2'
                                      style={{
                                        border: '1px solid #EAECF0',
                                        borderRadius: '4px',
                                        padding: '6px',
                                      }}
                                    >
                                      <img
                                        height={'32px'}
                                        src={creditCardLogo(item.brand)}
                                        alt=''
                                      />
                                    </span>
                                    <span>
                                      {item?.brand[0]?.toUpperCase() +
                                        item?.brand?.slice(1)?.toLocaleLowerCase() +
                                        t('finishedIn') +
                                        item?.lastNumberCard}
                                    </span>
                                  </>
                                ) : null}
                              </TableCell>
                              {/* <TableCell>
                        <div className='row'>
                          {item.canRefund ? (
                            <div className='col-auto px-0'>
                              <ModalReturnMoney
                                reservationId={reservation.id}
                                paymentDetail={reservation.paymentDetail}
                                operationId={item.operationId}
                              />
                            </div>
                          ) : null}
                        </div>
                      </TableCell> */}
                            </>
                          );
                        }}
                        rowClass='not-hover'
                      />
                    ) : (
                      t('payments.no')
                    )}
                  </ContanierSection>
                  <ModalDeleteProduct
                    callback={() => {
                      itemsQuery.refetch();
                      onAddSuccess();
                    }}
                    id={openDelete}
                    open={openDelete}
                    reservationId={reservationId}
                    setOpen={setOpenDelete}
                  />
                </Show.Else>
              </Show>
            </ConditionalWrapper>
          </FormProvider>
        </>
      ) : null}
      {addProductMutation.isSuccess ? (
        <Toast
          message={t('toast.success')}
          type={'SUCCESS'}
          onCallback={() => addProductMutation.reset()}
        />
      ) : null}
      {addProductMutation.isError ? (
        <Toast
          message={t('toast.error')}
          type={'ERROR'}
          onCallback={() => addProductMutation.reset()}
        />
      ) : null}
    </div>
  );
};

export default ReservationProductZone;
